import React from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../utils/constants';

function GettingStartedPage() {
  return (
    <div className="page">
      <div className="container">
        <div className="content-page">
          <h1>Getting Started</h1>

          <section className="content-section">
            <p>
              This page orients first-time visitors and helps them decide how they want to engage with QDN. 
              The goal is to keep things simple and low-pressure.
            </p>
          </section>

          <section className="content-section">
            <h2>Who This Space Is For</h2>
            <p>
              QDN is for LGBTQ+ people who touch data or tech in any way: analysts, data scientists, engineers, UX and user researchers, 
              students, activists, librarians, archivists, civic-tech organizers, and people who just like spreadsheets. Allies are welcome 
              to read and learn, but the space centers queer and trans needs and safety.
            </p>
            <p>
              This is a good fit for you if you care about ethical, community-centered data work, want to learn from other queer people in data, 
              or want a place to share work that might not feel safe or understood elsewhere.
            </p>
          </section>

          <section className="content-section">
            <h2>Create an Account and Profile</h2>
            <p>
              To participate in discussions or post resources, you'll need an account. When you sign up, you can choose a display name, 
              write a short bio, and add links (for example, a website, portfolio, or social profile).
            </p>
            <p>You are welcome to:</p>
            <ul>
              <li>Use a pseudonym or handle instead of your legal name.</li>
              <li>Share as much or as little personal information as feels safe.</li>
              <li>Update your profile over time as your comfort or circumstances change.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Browse Resources</h2>
            <p>
              The Resources section collects articles, guides, curated lists, and reflections from the community. You can:
            </p>
            <ul>
              <li>Filter or search by tags such as "ethics," "methods," "datasets," "education," "health," "organizing," and more.</li>
              <li>Open any resource to read the full post, see tags, and find related content.</li>
              <li>
                If a resource discusses specific datasets or case studies, look for notes about limitations, risks, or suggested use cases 
                instead of treating it as a neutral "best" dataset.
              </li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Use the Community Board</h2>
            <p>The community board is for more conversational, time-sensitive posts. Common types include:</p>
            <ul>
              <li>Announcements: calls for participation, zines or reports you're releasing, survey notices that meet ethical standards.</li>
              <li>Events: meetups, workshops, conferences, office hours, online panels.</li>
              <li>Discussions: questions, requests for feedback, reflections, or topic-based threads.</li>
            </ul>
            <p>Before posting, please:</p>
            <ul>
              <li>Read the <Link to={ROUTES.CODE_OF_CONDUCT}>Code of Conduct</Link>.</li>
              <li>Avoid sharing identifying information about third parties without consent.</li>
              <li>Include content warnings or tags for sensitive topics (for example, "anti-trans legislation," "medical trauma").</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Support QDN</h2>
            <p>If you want to help sustain QDN, you can:</p>
            <ul>
              <li>Use the "Support QDN" button to make a one-time donation via Stripe.</li>
              <li>Contribute content (guides, reflections, resources).</li>
              <li>Share QDN with other queer folks in data and tech.</li>
              <li>Offer feedback through the feedback thread or email.</li>
            </ul>
          </section>

          <section className="content-section">
            <h2>First Week Checklist</h2>
            <ul>
              <li>Read the <Link to={ROUTES.CODE_OF_CONDUCT}>Code of Conduct</Link>.</li>
              <li>Set up or update your profile.</li>
              <li>Bookmark the Resources and Community pages.</li>
              <li>Introduce yourself in the welcome thread, if that feels right.</li>
              <li>React to or comment on at least one resource or discussion that resonates with you.</li>
              <li>Optional: add one thing to your "someday" list to share here (a dataset critique, a reflection, a conference recap, etc.).</li>
            </ul>
          </section>

          <section className="content-section cta-section">
            <Link to={ROUTES.REGISTER} className="btn btn-primary">
              Create Account
            </Link>
            <Link to={ROUTES.CONTENT} className="btn btn-outline">
              Browse Resources
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}

export default GettingStartedPage;
